<template>
  <div class="min-h-screen bg-gray-100 p-4">
    <div class="max-w-7xl mx-auto">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <h1 class="text-2xl font-bold text-brown">Beyond Roast - Admin Dashboard</h1>
          <button
            @click="handleLogout"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Sair
          </button>
        </div>
        
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-10">
          <div class="text-green">Carregando dados...</div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-10">
          <div class="text-red-600">{{ error }}</div>
          <button 
            @click="loadSubmissions" 
            class="mt-4 bg-green text-white px-4 py-2 rounded-lg"
          >
            Tentar novamente
          </button>
        </div>

        <template v-else>
          <!-- Stats Summary -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-green/10 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-green">Total Leads</h3>
              <p class="text-2xl font-bold text-brown">{{ totalLeads }}</p>
            </div>
            <div class="bg-green/10 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-green">Média kg/semana</h3>
              <p class="text-2xl font-bold text-brown">{{ averageBeansPerWeek }}kg</p>
            </div>
            <div class="bg-green/10 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-green">Média Preço/kg</h3>
              <p class="text-2xl font-bold text-brown">R$ {{ averageRoastedPrice }}</p>
            </div>
          </div>

          <!-- No Data State -->
          <div v-if="submissions.length === 0" class="text-center py-10">
            <div class="text-gray-500">Nenhum dado encontrado</div>
          </div>

          <!-- Submissions Table -->
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grãos/Semana</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço Torrado</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="submission in submissions" :key="submission.formSubmissionId">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ submission.lead?.companyName }}</div>
                    <div class="text-sm text-gray-500">{{ submission.lead?.businessType }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">{{ submission.lead?.contactName }}</div>
                    <div class="text-sm text-gray-500">{{ submission.lead?.whatsapp || submission.lead?.email }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ submission.inputs?.beansPerWeek }}kg</div>
                    <div class="text-sm text-gray-500">por semana</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">R$ {{ formatNumber(submission.inputs?.precoRoastedPerKg) }}</div>
                    <div class="text-sm text-gray-500">por kg</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                      submission.lead?.wantsPilot ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    ]">
                      {{ submission.lead?.wantsPilot ? 'Pilot Interest' : 'Lead' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref as vueRef, onMounted } from 'vue';
import { getDatabase, ref as dbRef, get } from 'firebase/database';
import { getAuth, signOut } from 'firebase/auth';
import { useRouter } from 'vue-router';

export default {
  name: 'AdminDashboard',
  setup() {
    const router = useRouter();
    const auth = getAuth();
    const submissions = vueRef([]);
    const totalLeads = vueRef(0);
    const averageBeansPerWeek = vueRef(0);
    const averageRoastedPrice = vueRef(0);
    const isLoading = vueRef(true);
    const error = vueRef(null);

    const handleLogout = async () => {
      try {
        await signOut(auth);
        router.push('/admin/login');
      } catch (err) {
        console.error('Logout error:', err);
      }
    };

    const loadSubmissions = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const db = getDatabase();
        const statsRef = dbRef(db, 'roi-statistics');
        console.log('Fetching data from Firebase...');
        
        const snapshot = await get(statsRef);
        console.log('Data received:', snapshot.exists() ? 'Yes' : 'No');
        
        if (snapshot.exists()) {
          const data = [];
          let totalBeans = 0;
          let totalRoastedPrice = 0;
          let validSubmissions = 0;
          
          snapshot.forEach((child) => {
            const submission = child.val();
            if (submission.lead && submission.inputs) {
              data.push(submission);
              if (submission.inputs.beansPerWeek && submission.inputs.precoRoastedPerKg) {
                totalBeans += submission.inputs.beansPerWeek;
                totalRoastedPrice += submission.inputs.precoRoastedPerKg;
                validSubmissions++;
              }
            }
          });
          
          // Sort by newest first
          submissions.value = data.sort((a, b) => 
            new Date(b.formSubmittedAt || 0) - new Date(a.formSubmittedAt || 0)
          );
          
          totalLeads.value = data.length;
          averageBeansPerWeek.value = validSubmissions > 0 ? 
            Math.round(totalBeans / validSubmissions) : 0;
          averageRoastedPrice.value = validSubmissions > 0 ? 
            formatNumber(totalRoastedPrice / validSubmissions) : 0;
        }
      } catch (err) {
        console.error('Error loading submissions:', err);
        error.value = `Error loading data: ${err.message}`;
        if (err.code === 'PERMISSION_DENIED') {
          router.push('/admin/login');
        }
      } finally {
        isLoading.value = false;
      }
    };

    const formatNumber = (number) => {
      return number ? number.toLocaleString('pt-BR') : '0';
    };

    onMounted(() => {
      if (auth.currentUser) {
        loadSubmissions();
      } else {
        router.push('/admin/login');
      }
    });

    return {
      submissions,
      totalLeads,
      averageBeansPerWeek,
      averageRoastedPrice,
      formatNumber,
      isLoading,
      error,
      loadSubmissions,
      handleLogout
    };
  }
};
</script> 